import { Fragment } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Lightning, Target, Users } from "@phosphor-icons/react";

import { Progress } from "@/components/ui/progress";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SectionHeader } from "@/components/brand-intelligence/layout/heading";
import { VoiceComparison } from "@/components/brand-intelligence/charts/share-of-voice";
import { CompetitiveBenchmarking } from "@/components/brand-intelligence/charts/competitve-benchmarking";

import { useSession } from "@/hooks/use-session";
import { mEnterpriseBrandIntelligence } from "@/mock/brand-intelligence";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/dashboard/research/_layout/$id/competitive")({
  component: RouteComponent,
});

function RouteComponent() {
  return (
    <Fragment>
      <SectionHeader title="Competitive Intelligence" icon={<Target className="text-purple-500 size-6" />} iconClassName="bg-purple-500/10" />
      <div className="space-y-6">
        <Competitors />
        <VoiceComparison />
        <CompetitiveBenchmarking />
      </div>
    </Fragment>
  );
}

function Competitors() {
  const session = useSession();
  const brandIntelligence = mEnterpriseBrandIntelligence[session.user.email];
  const competitive = brandIntelligence.competitive_intel;

  if (!competitive.primary_competitors.length && !competitive.secondary_competitors.length && !competitive.positioning.disruption_watchlist.length) return null;

  return (
    <div className="flex justify-between gap-6">
      <PrimaryCompetitors />
      <SecondaryCompetitors />
      <EmergingDisruptors />
    </div>
  );
}

function PrimaryCompetitors() {
  const session = useSession();

  const brandIntelligence = mEnterpriseBrandIntelligence[session.user.email];
  const competitive = brandIntelligence.competitive_intel;

  const normalizeThreatLevel = (level?: string): "High Threat" | "Medium Threat" | "Low Threat" => {
    const l = (level ?? "").toLowerCase();
    if (l.includes("high")) return "High Threat";
    if (l.includes("low")) return "Low Threat";
    if (l.includes("medium")) return "Medium Threat";
    return "Medium Threat";
  };

  const threatColors = (threat: "High Threat" | "Medium Threat" | "Low Threat") => {
    if (threat === "High Threat") return { background: "bg-destructive/10", icon: "text-destructive" };
    if (threat === "Low Threat") return { background: "bg-success/10", icon: "text-success" };
    return { background: "bg-warning/10", icon: "text-warning" };
  };

  type PrimaryCompetitor = {
    name: string;
    marketShare: number;
    threat: "High Threat" | "Medium Threat" | "Low Threat";
    colors: { background: string; icon: string };
    sentiment: number | null;
  };

  const primaryCompetitors: PrimaryCompetitor[] = competitive.primary_competitors
    .filter((c) => c.market_share !== 0)
    .map((c) => {
      const threat = normalizeThreatLevel(c.threat_level);
      return {
        name: c.name,
        marketShare: Math.round(c.market_share),
        threat,
        colors: threatColors(threat),
        sentiment: c.sentiment,
      };
    });

  if (!primaryCompetitors.length) return null;

  return (
    <Card className="flex-1 basis-0">
      <CardHeader className="flex flex-row items-center gap-2">
        <Target className="size-5 text-warning shrink-0" />
        <CardTitle className="text-sm font-semibold">Primary Competitors</CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-4 max-h-96 overflow-y-auto scrollbar-hidden">
        {primaryCompetitors.map((competitor) => (
          <Card key={competitor.name} className="py-0">
            <CardContent className="py-4 px-6 flex items-center justify-between gap-4">
              <div className="min-w-0 space-y-0.5">
                <p className="text-sm font-semibold text-foreground">{competitor.name}</p>
                <p className="text-xs text-muted-foreground">Market Share: {competitor.marketShare}%</p>
              </div>
              <div className="flex flex-col items-end shrink-0 text-right">
                <span className={cn("rounded-sm px-2.5 py-1 text-xs font-medium", competitor.colors.background, competitor.colors.icon)}>{competitor.threat}</span>
                <p className="text-xs text-muted-foreground mt-1.5">Sentiment: {competitor.sentiment != null ? `${Math.round(competitor.sentiment)}%` : "N/A"}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </CardContent>
    </Card>
  );
}

function SecondaryCompetitors() {
  const session = useSession();

  const brandIntelligence = mEnterpriseBrandIntelligence[session.user.email];
  const competitive = brandIntelligence.competitive_intel;

  const normalizeThreatLevel = (level?: string): "High Threat" | "Medium Threat" | "Low Threat" => {
    const l = (level ?? "").toLowerCase();
    if (l.includes("high")) return "High Threat";
    if (l.includes("low")) return "Low Threat";
    if (l.includes("medium")) return "Medium Threat";
    return "Medium Threat";
  };

  const threatColors = (threat: "High Threat" | "Medium Threat" | "Low Threat") => {
    if (threat === "High Threat") return { background: "bg-destructive/10", icon: "text-destructive" };
    if (threat === "Low Threat") return { background: "bg-success/10", icon: "text-success" };
    return { background: "bg-warning/10", icon: "text-warning" };
  };

  type SecondaryCompetitor = {
    name: string;
    marketShare: number;
    threat: "High Threat" | "Medium Threat" | "Low Threat";
    colors: { background: string; icon: string };
    sentiment: number | null;
  };

  const secondaryCompetitors: SecondaryCompetitor[] = competitive.secondary_competitors
    .filter((c) => c.market_share !== 0)
    .map((c) => {
      const threat = normalizeThreatLevel(c.threat_level);
      return {
        name: c.name,
        marketShare: Math.round(c.market_share),
        threat,
        colors: threatColors(threat),
        sentiment: c.sentiment,
      };
    });

  if (!secondaryCompetitors.length) return null;

  return (
    <Card className="flex-1 basis-0">
      <CardHeader className="flex flex-row items-center gap-2">
        <Users className="size-5 text-warning shrink-0" />
        <CardTitle className="text-sm font-semibold">Secondary Competitors</CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-4 max-h-96 overflow-y-auto scrollbar-hidden">
        {secondaryCompetitors.map((competitor) => (
          <Card key={competitor.name} className="py-0">
            <CardContent className="py-4 px-6 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-foreground">{competitor.name}</p>
                <p className="text-xs text-muted-foreground">
                  <span className={cn("rounded-sm px-2 py-0.5 text-[0.65rem] font-medium", competitor.colors.background, competitor.colors.icon)}>{competitor.threat}</span>
                  {competitor.sentiment != null && competitor.sentiment > 0 ? <span className="ml-2">Sentiment: {Math.round(competitor.sentiment)}/100</span> : null}
                </p>
              </div>
              <div className="flex items-center gap-3 min-w-0">
                <Progress value={competitor.marketShare} className="h-2 flex-1 bg-muted" indicatorClassName="bg-success rounded-full" />
                <span className="text-xs text-muted-foreground shrink-0 tabular-nums">{competitor.marketShare}% share</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </CardContent>
    </Card>
  );
}

function EmergingDisruptors() {
  const session = useSession();

  const brandIntelligence = mEnterpriseBrandIntelligence[session.user.email];
  const competitive = brandIntelligence.competitive_intel;

  type EmergingDisruptor = { name: string; description: string };

  const emergingDisruptors: EmergingDisruptor[] = competitive.positioning.disruption_watchlist.map((name) => ({
    name,
    description: "Disruption watchlist",
  }));
  if (!emergingDisruptors.length) return null;

  return (
    <Card className="flex-1 basis-0">
      <CardHeader className="flex flex-row items-center gap-2">
        <Lightning className="size-5 text-warning shrink-0" />
        <CardTitle className="text-sm font-semibold">Emerging Disruptors</CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-4 max-h-96 overflow-y-auto scrollbar-hidden">
        {emergingDisruptors.map((disruptor) => (
          <Card key={disruptor.name} className="py-0">
            <CardContent className="py-4 px-6">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-foreground">{disruptor.name}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </CardContent>
    </Card>
  );
}
